// T6b — Conversation hub API wrapper. Lives outside the frozen api.ts. Wraps
// the T6a backend endpoints (`/api/communications/conversations` + nested).
//
// Types mirror the rollup table + messages timeline shapes from T6a.3.

import { api } from './apiClient';

export interface Conversation {
  id: number;
  counterparty_phone: string;
  counterparty_name: string | null;
  counterparty_contact_id: number | null;
  phone_number_id: number;
  last_message_at: string;
  last_message_type: 'call' | 'sms' | 'voicemail' | string;
  last_message_preview: string | null;
  last_message_direction: 'inbound' | 'outbound' | string;
  last_message_outcome: string | null;
  unread_count: number;
  status: 'open' | 'closed' | 'archived' | string;
}

export interface ConversationMessage {
  id: number;
  type: 'call' | 'sms' | 'voicemail' | string;
  direction: 'inbound' | 'outbound' | string;
  at: string;
  duration_seconds: number | null;
  body: string | null;
  notes: string | null;
  outcome: string | null;
  tags: string | null;
  voicemail_url: string | null;
  voicemail_transcript: string | null;
  recording_url: string | null;
  ai_summary: string | null;
  // PHONE-C4 — what a transcribed call leaves behind. `transcript_state` is
  // computed server-side: only the server knows whether the LINE has
  // transcription switched on, so the honest "why is there nothing here"
  // answer cannot be derived in the browser.
  transcript_text?: string | null;
  transcribed_at?: string | null;
  ai_next_steps_json?: string | null;
  sales_score_json?: string | null;
  transcript_state?: 'ready' | 'silent' | 'not_recorded' | 'too_short' | 'disabled' | 'pending' | null;
  caller_name: string | null;
  // SMS delivery status from the Twilio status callback (null on call rows).
  status?: string | null;
}

export interface ConversationListParams {
  phone_number_id?: number[] | string;
  status?: 'open' | 'closed' | 'archived' | 'all';
  search?: string;
  channel?: 'all' | 'text' | 'call' | 'voicemail';
  limit?: number;
  offset?: number;
}

export interface ConversationSummary {
  unread: number;
  needs_reply: number;
  unheard_voicemail: number;
}

export interface ConversationContext {
  contact: {
    id: number; first_name: string; last_name: string; title: string | null;
    company_name: string | null; tags: string | null; client_card_id: string | null; email: string | null;
  } | null;
  deal: {
    id: number; name: string; amount: number; currency: string; pipeline_id: number | null;
    pipeline_stage: string | null; status: string; owner_name: string | null;
  } | null;
}

export interface CommsLine {
  id: number;
  phone_number: string;
  friendly_name: string | null;
  is_default: number;
  capabilities_voice: number;
  capabilities_sms: number;
}

export interface ConversationMessagesParams {
  limit?: number;
  offset?: number;
  before?: string;
  after?: string;
}

function paramsForList(p?: ConversationListParams) {
  if (!p) return {};
  const out: Record<string, any> = {};
  if (p.phone_number_id) {
    out.phone_number_id = Array.isArray(p.phone_number_id) ? p.phone_number_id.join(',') : p.phone_number_id;
  }
  if (p.status) out.status = p.status;
  if (p.search) out.search = p.search;
  if (p.channel && p.channel !== 'all') out.channel = p.channel;
  if (p.limit != null) out.limit = p.limit;
  if (p.offset != null) out.offset = p.offset;
  return out;
}

// T6b-followup — Paginated endpoints land double-wrapped because the backend
// composes resp.success() + paginatedResponse() (the latter already carries
// its own { success, data, total, page, limit, offset } envelope). The
// existing communicationsApi.getCallLogs handles this with a flatten:
//   .then(r => ({ success: r.data.success, ...r.data.data }))
// Mirror that here so consumers see the flat shape { success, data: [...],
// total, limit, offset }. Single-row endpoints stay simple .then(r => r.data).
function flattenPaginated<T>(r: any): { success: boolean; data: T[]; total: number; limit: number; offset: number } {
  return { success: r.data.success, ...r.data.data };
}

// T6b-followup — Twilio recording URLs require Basic Auth, so the browser
// can't load them directly via <audio src=…> without popping a sign-in
// dialog. Route them through the backend proxy that injects credentials
// server-side and streams audio back same-origin. Non-Twilio URLs (e.g.,
// pre-existing recordings hosted elsewhere) pass through unchanged.
//
// Also: Twilio's webhook callbacks store the Recordings URL WITHOUT an
// extension (https://api.twilio.com/.../Recordings/{Sid}). Hitting that
// base URL returns XML metadata, not audio — which is why <audio> elements
// were reading 0:00 duration. Append `.mp3` so the proxy fetches the
// actual MP3 stream.
export function recordingProxyUrl(rawUrl: string | null | undefined): string | undefined {
  if (!rawUrl) return undefined;
  try {
    const u = new URL(rawUrl);
    if (u.hostname === 'api.twilio.com') {
      // Append .mp3 if a Recordings/{sid} URL lacks an audio extension.
      let target = rawUrl;
      const path = u.pathname;
      const isRecording = /\/Recordings\/[^/]+$/.test(path);
      const hasExt = /\.(mp3|wav|m4a|ogg)$/i.test(path);
      if (isRecording && !hasExt) {
        target = `${u.origin}${path}.mp3${u.search}`;
      }
      return `/api/communications/recordings/proxy?url=${encodeURIComponent(target)}`;
    }
  } catch { /* fall through — return raw */ }
  return rawUrl;
}

export const conversationsApi = {
  list: (p?: ConversationListParams): Promise<{ success: boolean; data: Conversation[]; total: number; limit: number; offset: number }> =>
    api.get('/communications/conversations', { params: paramsForList(p) }).then((r) => flattenPaginated<Conversation>(r)),

  get: (id: number): Promise<{ success: boolean; data: Conversation }> =>
    api.get(`/communications/conversations/${id}`).then((r) => r.data),

  messages: (id: number, p?: ConversationMessagesParams): Promise<{ success: boolean; data: ConversationMessage[]; total: number; limit: number; offset: number }> =>
    api.get(`/communications/conversations/${id}/messages`, { params: p || {} }).then((r) => flattenPaginated<ConversationMessage>(r)),

  markRead: (id: number): Promise<{ success: boolean; data: Conversation }> =>
    api.post(`/communications/conversations/${id}/mark-read`).then((r) => r.data),

  findOrCreate: (data: { counterparty_phone: string; phone_number_id: number }): Promise<{ success: boolean; data: Conversation }> =>
    api.post('/communications/conversations', data).then((r) => r.data),

  // Redesign 2026-07 — triage counts for the hub's pills / badges / empty state.
  summary: (): Promise<{ success: boolean; data: ConversationSummary }> =>
    api.get('/communications/conversations/summary').then((r) => r.data),

  // Contact + linked-deal panel data, resolved server-side (WIRE GATE weld).
  context: (id: number): Promise<{ success: boolean; data: ConversationContext }> =>
    api.get(`/communications/conversations/${id}/context`).then((r) => r.data),

  // Attach (or clear, contactId=null) the CRM contact from the context panel.
  linkContact: (id: number, contactId: number | null): Promise<{ success: boolean; data: any }> =>
    api.patch(`/communications/conversations/${id}/link-contact`, { contact_id: contactId }).then((r) => r.data),

  // "My lines" — union of all three assignment stores, gated by tab:comms
  // (replaces GET /phones/assignments/me for the comms hub; that route sits
  // behind tab:phones and reads only user_phone_assignments).
  myLines: (): Promise<{ success: boolean; data: CommsLine[] }> =>
    api.get('/communications/lines/mine').then((r) => r.data),

  // SMS quick-reply templates (kv_store-seeded; shared with mobile M6).
  quickTemplates: (): Promise<{ success: boolean; data: Array<{ label: string; body: string }> }> =>
    api.get('/communications/sms/quick-templates').then((r) => r.data),
};
